import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Library, Plus, Trash2, Pencil, X, Users, CheckCircle2, BookOpen } from "lucide-react";
import { GRADE_GROUPS } from "@/lib/subjects";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const emptySection = () => ({ name: "", subject: "", page_start: "", page_end: "", questions: "" });
const emptyResource = () => ({ name: "", category: "Genel", sections: [emptySection()] });

export default function KaynakKutuphanesi() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const isAdmin = user?.role === "admin";
  const isEditor = user?.role === "admin" || user?.role === "koc";

  const [subjects, setSubjects] = useState([]);
  const [resources, setResources] = useState([]);
  const [students, setStudents] = useState([]);

  const [studentId, setStudentId] = useState(params.get("student") || null);
  const [studentName, setStudentName] = useState(params.get("name") || "");
  const [assignments, setAssignments] = useState([]);

  const [dialog, setDialog] = useState(null); // resource draft (create/edit)
  const [assignFor, setAssignFor] = useState(null); // resource being assigned
  const [assignSel, setAssignSel] = useState([]);
  const [assignGrade, setAssignGrade] = useState(Number(params.get("grade")) || 5);
  const [assignSection, setAssignSection] = useState(params.get("section") || "");

  useEffect(() => {
    api.get("/subjects").then(({ data }) => setSubjects(data));
    if (isEditor) {
      api.get("/resources").then(({ data }) => setResources(data));
      api.get("/students").then(({ data }) => setStudents(data));
    }
    if (user?.role === "ogrenci" || user?.role === "veli") {
      api.get("/my/context").then(({ data }) => { setStudentId(data.student_id); setStudentName(data.student_name || ""); });
    }
  }, [user, isEditor]);

  useEffect(() => {
    if (isEditor && studentId) {
      const st = students.find((s) => s.id === studentId);
      if (st) setStudentName(st.name);
    }
  }, [isEditor, studentId, students]);

  const loadResources = useCallback(() => { if (isEditor) api.get("/resources").then(({ data }) => setResources(data)); }, [isEditor]);
  const loadAssignments = useCallback(() => {
    if (!studentId) { setAssignments([]); return; }
    api.get("/resources/assignments", { params: { student_id: studentId } }).then(({ data }) => setAssignments(data));
  }, [studentId]);
  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  // ---- resource CRUD ----
  const saveResource = async () => {
    if (!dialog.name.trim()) return toast.error("Kaynak adı girin");
    try {
      if (dialog.id) await api.put(`/resources/${dialog.id}`, dialog);
      else await api.post("/resources", dialog);
      toast.success("Kaynak kaydedildi ✓");
      setDialog(null); loadResources();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeResource = async (id) => {
    if (!window.confirm("Bu kaynak ve atamaları silinsin mi?")) return;
    try { await api.delete(`/resources/${id}`); toast.success("Silindi"); loadResources(); loadAssignments(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setSec = (i, field, v) => setDialog((d) => ({ ...d, sections: d.sections.map((s, j) => (j === i ? { ...s, [field]: v } : s)) }));
  const addSec = () => setDialog((d) => ({ ...d, sections: [...d.sections, emptySection()] }));
  const delSec = (i) => setDialog((d) => (d.sections.length <= 1 ? d : { ...d, sections: d.sections.filter((_, j) => j !== i) }));

  // ---- assign ----
  const openAssign = (r) => {
    setAssignFor(r);
    api.get("/resources/assignments").catch(() => {});
    setAssignSel([]);
  };
  const doAssign = async () => {
    if (!assignSel.length) return toast.error("En az bir öğrenci seçin");
    try {
      const { data } = await api.post(`/resources/${assignFor.id}/assign`, { student_ids: assignSel });
      toast.success(`${data.assigned} öğrenciye atandı ✓`);
      setAssignFor(null); loadResources(); loadAssignments();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- progress ----
  const patchProgress = async (aid, section_index, patch) => {
    try {
      const { data } = await api.patch(`/resources/assignments/${aid}/progress`, { section_index, ...patch });
      setAssignments((prev) => prev.map((a) => (a.id === aid ? { ...a, progress: data.progress } : a)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeAssignment = async (aid) => {
    if (!window.confirm("Bu atama kaldırılsın mı?")) return;
    try { await api.delete(`/resources/assignments/${aid}`); toast.success("Atama kaldırıldı"); loadAssignments(); loadResources(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600"><Library className="h-6 w-6" /></div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Kaynak Yönetimi</p>
            <h1 className="font-display text-3xl font-extrabold text-slate-900">Kaynak Kütüphanesi</h1>
            <p className="text-slate-500">Kaynak tanımla (bölüm + sayfa aralığı + soru), öğrenciye ata, ilerlemeyi gir/düzenle.</p>
          </div>
        </div>
        {isAdmin && (
          <button data-testid="kk-new-btn" onClick={() => setDialog(emptyResource())} className="flex items-center gap-2 rounded-xl bg-[#1b2559] px-5 py-3 font-bold text-white shadow-lg transition hover:bg-[#243069] active:scale-95">
            <Plus className="h-5 w-5" /> Yeni Kaynak
          </button>
        )}
      </div>

      {/* Kaynak listesi (admin/koç) */}
      {isEditor && (
        <div className="space-y-3">
          <h3 className="font-display text-lg font-bold text-slate-900">Tanımlı Kaynaklar</h3>
          {resources.length === 0 ? (
            <div data-testid="kk-empty" className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-400">Henüz kaynak tanımlanmadı.</div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {resources.map((r) => (
                <div key={r.id} data-testid={`kk-resource-${r.id}`} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-bold text-slate-900">{r.name}</div>
                      <div className="text-xs text-slate-400">{r.category} · {r.sections?.length || 0} bölüm · {r.assigned_count || 0} atama</div>
                    </div>
                    <div className="flex gap-1">
                      <button data-testid={`kk-assign-${r.id}`} onClick={() => openAssign(r)} className="flex items-center gap-1 rounded-lg bg-indigo-50 px-2.5 py-1.5 text-xs font-bold text-indigo-700 transition hover:bg-indigo-100"><Users className="h-3.5 w-3.5" /> Ata</button>
                      {isAdmin && <>
                        <button data-testid={`kk-edit-${r.id}`} onClick={() => setDialog(JSON.parse(JSON.stringify(r)))} className="rounded-lg p-2 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600"><Pencil className="h-4 w-4" /></button>
                        <button data-testid={`kk-delete-${r.id}`} onClick={() => removeResource(r.id)} className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                      </>}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(r.sections || []).slice(0, 6).map((s, i) => (
                      <span key={i} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{s.name || `Bölüm ${i + 1}`}{s.subject ? ` · ${s.subject}` : ""}{s.page_start ? ` (${s.page_start}-${s.page_end})` : ""}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Öğrenci seçimi (admin/koç) */}
      {isEditor && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <label className="mb-1 block text-sm font-semibold text-slate-700">Öğrenci ilerlemesi</label>
          <select data-testid="kk-student-select" value={studentId || ""} onChange={(e) => setStudentId(e.target.value || null)} className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 outline-none focus:border-indigo-400 sm:max-w-sm">
            <option value="">Öğrenci seçin…</option>
            {students.map((s) => <option key={s.id} value={s.id}>{s.name}{s.grade ? ` (${s.grade}. Sınıf)` : ""}</option>)}
          </select>
        </div>
      )}

      {/* Atanmış kaynaklar + ilerleme */}
      {studentId && (
        <div className="space-y-3">
          <h3 className="font-display text-lg font-bold text-slate-900">Atanan Kaynaklar{studentName ? ` · ${studentName}` : ""}</h3>
          {assignments.length === 0 ? (
            <div data-testid="kk-assign-empty" className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-400">Atanmış kaynak yok.</div>
          ) : assignments.map((a) => {
            const secs = a.resource?.sections || [];
            const done = secs.filter((_, i) => a.progress?.[i]?.done).length;
            return (
              <div key={a.id} data-testid={`kk-assignment-${a.id}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between gap-2 bg-slate-50 px-5 py-3">
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-emerald-600" />
                    <span className="font-bold text-slate-900">{a.resource?.name}</span>
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700">{done}/{secs.length} tamamlandı</span>
                  </div>
                  {isEditor && <button data-testid={`kk-unassign-${a.id}`} onClick={() => removeAssignment(a.id)} className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px] text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                        <th className="px-4 py-2">Bölüm</th><th className="px-3 py-2">Ders</th><th className="px-3 py-2">Sayfa</th><th className="px-3 py-2 text-center">Soru</th><th className="px-3 py-2 text-center">Çözülen</th><th className="px-3 py-2 text-center">Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {secs.map((s, i) => {
                        const p = a.progress?.[i] || {};
                        return (
                          <tr key={i} className="border-b border-slate-100">
                            <td className="px-4 py-2 font-semibold text-slate-800">{s.name || `Bölüm ${i + 1}`}</td>
                            <td className="px-3 py-2 text-slate-600">{s.subject || "—"}</td>
                            <td className="px-3 py-2 text-slate-600">{s.page_start ? `${s.page_start}-${s.page_end}` : "—"}</td>
                            <td className="px-3 py-2 text-center text-slate-600">{s.questions || "—"}</td>
                            <td className="px-3 py-2 text-center">
                              {isEditor ? (
                                <input data-testid={`kk-cozulen-${a.id}-${i}`} type="number" min={0} defaultValue={p.cozulen ?? ""} onBlur={(e) => patchProgress(a.id, i, { cozulen: Number(e.target.value) || 0 })} className="w-16 rounded border border-slate-200 px-2 py-1 text-center outline-none focus:border-indigo-400" />
                              ) : (
                                <span data-testid={`kk-cozulen-${a.id}-${i}`} className="font-semibold text-slate-700">{p.cozulen ?? 0}</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {isEditor ? (
                                <button data-testid={`kk-done-${a.id}-${i}`} onClick={() => patchProgress(a.id, i, { done: !p.done })} className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-bold transition ${p.done ? "bg-emerald-600 text-white" : "border border-slate-200 bg-white text-slate-500 hover:bg-slate-50"}`}>
                                  <CheckCircle2 className="h-3.5 w-3.5" /> {p.done ? "Bitti" : "İşaretle"}
                                </button>
                              ) : (
                                <span data-testid={`kk-status-${a.id}-${i}`} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${p.done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                                  <CheckCircle2 className="h-3.5 w-3.5" /> {p.done ? "Tamamlandı" : "Bekliyor"}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Yeni/Düzenle Kaynak diyaloğu */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{dialog?.id ? "Kaynağı Düzenle" : "Yeni Kaynak"}</DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Bölümleri sayfa aralığı ve toplam soru sayısıyla tanımla.</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className={lbl}>Kaynak Adı</label>
                  <input data-testid="kk-name" value={dialog.name} onChange={(e) => setDialog({ ...dialog, name: e.target.value })} placeholder="Örn: Hız Yayınları Matematik" className={inp} />
                </div>
                <div>
                  <label className={lbl}>Kategori</label>
                  <input data-testid="kk-category" value={dialog.category} onChange={(e) => setDialog({ ...dialog, category: e.target.value })} placeholder="Genel" className={inp} />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-slate-700">İçindekiler (Bölümler)</label>
                <button data-testid="kk-add-section" onClick={addSec} className="flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 active:scale-95"><Plus className="h-4 w-4" /> Bölüm Ekle</button>
              </div>
              <div className="space-y-2">
                {dialog.sections.map((s, i) => (
                  <div key={i} data-testid={`kk-section-${i}`} className="grid grid-cols-12 items-end gap-2 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                    <div className="col-span-12 sm:col-span-4">
                      <label className={lblx}>Bölüm Adı</label>
                      <input data-testid={`kk-sec-name-${i}`} value={s.name} onChange={(e) => setSec(i, "name", e.target.value)} placeholder="1. Ünite" className={inpx} />
                    </div>
                    <div className="col-span-6 sm:col-span-3">
                      <label className={lblx}>Ders</label>
                      <select data-testid={`kk-sec-subject-${i}`} value={s.subject} onChange={(e) => setSec(i, "subject", e.target.value)} className={inpx}>
                        <option value="">Seçin…</option>
                        {subjects.map((sub) => <option key={sub.id} value={sub.name}>{sub.name}</option>)}
                      </select>
                    </div>
                    <div className="col-span-3 sm:col-span-1"><label className={lblx}>Sayfa Bş.</label><input data-testid={`kk-sec-ps-${i}`} value={s.page_start} onChange={(e) => setSec(i, "page_start", e.target.value)} className={inpx} /></div>
                    <div className="col-span-3 sm:col-span-1"><label className={lblx}>Sayfa Bt.</label><input data-testid={`kk-sec-pe-${i}`} value={s.page_end} onChange={(e) => setSec(i, "page_end", e.target.value)} className={inpx} /></div>
                    <div className="col-span-4 sm:col-span-2"><label className={lblx}>Soru</label><input data-testid={`kk-sec-q-${i}`} value={s.questions} onChange={(e) => setSec(i, "questions", e.target.value)} className={inpx} /></div>
                    <div className="col-span-2 sm:col-span-1 flex justify-end pb-1">
                      <button data-testid={`kk-sec-del-${i}`} onClick={() => delSec(i)} className="rounded-lg p-2 text-rose-500 transition hover:bg-rose-50"><X className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>

              <button data-testid="kk-save-btn" onClick={saveResource} className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#1b2559] py-3.5 font-bold text-white transition hover:bg-[#243069] active:scale-95">
                <Plus className="h-5 w-5" /> Kaydet
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Atama diyaloğu */}
      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent className="max-h-[80vh] max-w-lg overflow-y-auto rounded-2xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">Öğrenciye Ata</DialogTitle></DialogHeader>
          {assignFor && (
            <div className="space-y-3">
              <p className="text-sm text-slate-500"><b>{assignFor.name}</b> kaynağını öğrenci ya da sınıf seçerek ata.</p>
              <div className="flex flex-wrap items-end gap-2 rounded-xl border border-indigo-200 bg-indigo-50/50 p-3">
                <div>
                  <label className="mb-1 block text-[11px] font-semibold text-slate-500">Sınıf</label>
                  <select data-testid="kk-assign-grade" value={assignGrade} onChange={(e) => setAssignGrade(Number(e.target.value))} className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm outline-none focus:border-indigo-400">
                    {GRADE_GROUPS.map((grp) => <optgroup key={grp.label} label={grp.label}>{grp.grades.map((g) => <option key={g} value={g}>{g}. Sınıf</option>)}</optgroup>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-semibold text-slate-500">Şube</label>
                  <input data-testid="kk-assign-section" value={assignSection} onChange={(e) => setAssignSection(e.target.value)} placeholder="Tümü" className="w-24 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm outline-none focus:border-indigo-400" />
                </div>
                <button data-testid="kk-assign-select-class" onClick={() => {
                  const ids = students.filter((s) => s.grade === assignGrade && (!assignSection || (s.section || "").toLowerCase() === assignSection.toLowerCase())).map((s) => s.id);
                  if (!ids.length) return toast.error("Bu sınıfta öğrenci yok");
                  setAssignSel(ids);
                  toast.success(`${ids.length} öğrenci seçildi`);
                }} className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 active:scale-95"><Users className="h-4 w-4" /> Sınıfı Seç</button>
                <button data-testid="kk-assign-clear" onClick={() => setAssignSel([])} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50">Temizle</button>
              </div>
              <div className="max-h-60 space-y-1.5 overflow-y-auto">
                {students.map((s) => {
                  const on = assignSel.includes(s.id);
                  return (
                    <button key={s.id} data-testid={`kk-assign-student-${s.id}`} onClick={() => setAssignSel((prev) => on ? prev.filter((x) => x !== s.id) : [...prev, s.id])} className={`flex w-full items-center justify-between rounded-xl border px-4 py-2.5 text-left transition ${on ? "border-indigo-400 bg-indigo-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                      <span className="font-semibold text-slate-800">{s.name} <span className="text-xs text-slate-400">{s.grade ? `${s.grade}. Sınıf${s.section ? "/" + s.section : ""}` : ""}</span></span>
                      {on && <CheckCircle2 className="h-5 w-5 text-indigo-600" />}
                    </button>
                  );
                })}
              </div>
              <button data-testid="kk-assign-confirm" onClick={doAssign} className="w-full rounded-xl bg-indigo-600 py-3 font-bold text-white transition hover:bg-indigo-700 active:scale-95">{assignSel.length} Öğrenciye Ata</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const lbl = "mb-1 block text-sm font-semibold text-slate-700";
const inp = "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100";
const lblx = "mb-1 block text-[11px] font-semibold text-slate-500";
const inpx = "w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm outline-none focus:border-indigo-400";
